import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity as ActivityIcon,
  AlertTriangle,
  BarChart3,
  GitBranch,
  Layers,
  RefreshCw,
  ShieldCheck,
  Table2,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Papa from 'papaparse';

import { Header } from './Header';
import { ScheduleUpload } from './dashboard/ScheduleUpload';
import { CommandBar, EMPTY_FILTERS, type Filters } from './dashboard/CommandBar';
import { KpiTile, type KpiTone } from './dashboard/KpiTile';
import { DcmaScorecard } from './dashboard/DcmaScorecard';
import { FloatHistogram } from './dashboard/FloatHistogram';
import { EvmCurve } from './dashboard/EvmCurve';
import { SlipWaterfall } from './dashboard/SlipWaterfall';
import { WbsTreemap } from './dashboard/WbsTreemap';
import { CriticalPathNetwork } from './dashboard/CriticalPathNetwork';
import { ScheduleGantt } from './dashboard/ScheduleGantt';
import { ActivityTable } from './dashboard/ActivityTable';
import { ActivityInspector } from './dashboard/ActivityInspector';
import {
  TooltipProvider,
  formatCurrency,
  formatDate,
} from './dashboard/chartPrimitives';

import { DCMAAnalyzer, type DCMAResult } from '../services/dcmaAnalyzer';
import {
  compareToBaseline,
  computeCriticalPath,
  computeEvm,
  computeFloatDistribution,
  computeHeadlines,
  computeWbsRollup,
} from '../services/analytics';
import { chatService } from '../services/chatService';
import type { Activity, ScheduleModel } from '../types/schedule';

type TabKey = 'overview' | 'quality' | 'critical' | 'baseline' | 'cost' | 'activities';

const TABS: { key: TabKey; label: string; icon: typeof BarChart3 }[] = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'quality', label: 'Schedule Quality', icon: ShieldCheck },
  { key: 'critical', label: 'Critical Path', icon: GitBranch },
  { key: 'baseline', label: 'Baseline Variance', icon: Layers },
  { key: 'cost', label: 'Resources & Cost', icon: ActivityIcon },
  { key: 'activities', label: 'Activities', icon: Table2 },
];

export function ReportPage() {
  const [schedule, setSchedule] = useState<ScheduleModel | null>(null);
  const [baseline, setBaseline] = useState<ScheduleModel | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [tab, setTab] = useState<TabKey>('overview');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [narrative, setNarrative] = useState('');
  const [narrativeState, setNarrativeState] = useState<'idle' | 'loading' | 'error'>('idle');

  const searchRef = useRef<HTMLInputElement>(null);

  /* ── Derived analytics ───────────────────────────────────────────────── */

  const filtered = useMemo<Activity[]>(() => {
    if (!schedule) return [];

    const pinned = filters.pinnedIds ? new Set(filters.pinnedIds) : null;
    const search = filters.search.trim().toLowerCase();
    const from = filters.startDate ? new Date(filters.startDate) : null;
    const to = filters.endDate ? new Date(`${filters.endDate}T23:59:59`) : null;

    // Descendant WBS ids, so filtering a parent includes its children.
    let wbsScope: Set<string> | null = null;
    if (filters.wbsId !== 'all') {
      wbsScope = new Set([filters.wbsId]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const node of schedule.wbs) {
          if (node.parentId && wbsScope.has(node.parentId) && !wbsScope.has(node.id)) {
            wbsScope.add(node.id);
            grew = true;
          }
        }
      }
    }

    const [codeType, codeValue] =
      filters.activityCode !== 'all' ? filters.activityCode.split('::') : [null, null];

    return schedule.activities.filter((activity) => {
      if (pinned && !pinned.has(activity.id)) return false;

      if (search) {
        const haystack = `${activity.code} ${activity.name}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }

      if (filters.status !== 'all' && activity.status !== filters.status) return false;

      if (filters.criticality === 'critical' && !activity.isCritical) return false;
      if (filters.criticality === 'noncritical' && activity.isCritical) return false;

      if (wbsScope && (!activity.wbsId || !wbsScope.has(activity.wbsId))) return false;

      if (codeType && codeValue && activity.activityCodes[codeType] !== codeValue) {
        return false;
      }

      // The date window filters on finish, which is the date reviewers ask about.
      if (from && (!activity.finish || activity.finish < from)) return false;
      if (to && (!activity.finish || activity.finish > to)) return false;

      return true;
    });
  }, [schedule, filters]);

  const headlines = useMemo(() => computeHeadlines(filtered), [filtered]);

  // DCMA assesses the whole schedule — a filtered subset would produce ratios
  // that look like the programme but are not.
  const dcma = useMemo<DCMAResult | null>(
    () => (schedule ? new DCMAAnalyzer(schedule, baseline ?? undefined).analyze() : null),
    [schedule, baseline]
  );

  const evm = useMemo(
    () => (schedule ? computeEvm(schedule, baseline ?? undefined) : null),
    [schedule, baseline]
  );

  const floatBins = useMemo(() => computeFloatDistribution(filtered), [filtered]);

  const wbsRollup = useMemo(
    () => (schedule ? computeWbsRollup(schedule, filtered) : []),
    [schedule, filtered]
  );

  const criticalPath = useMemo(
    () => (schedule ? computeCriticalPath(schedule) : null),
    [schedule]
  );

  const comparison = useMemo(
    () => (schedule && baseline ? compareToBaseline(schedule, baseline) : null),
    [schedule, baseline]
  );

  const baselineByCode = useMemo(
    () =>
      baseline ? new Map(baseline.activities.map((a) => [a.code, a])) : undefined,
    [baseline]
  );

  const selected = selectedId ? (schedule?.activityById.get(selectedId) ?? null) : null;

  /* ── Interaction ─────────────────────────────────────────────────────── */

  const drillThrough = useCallback((activityIds: string[], label: string) => {
    setFilters((prev) => ({ ...prev, pinnedIds: activityIds, pinnedLabel: label }));
    setTab('activities');
    setSelectedId(null);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT';

      if (event.key === '/' && !typing) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (typing) return;

      const index = Number(event.key);
      if (index >= 1 && index <= TABS.length) setTab(TABS[index - 1].key);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleExport = useCallback(() => {
    const rows = filtered.map((activity) => ({
      'Activity ID': activity.code,
      'Activity Name': activity.name,
      Status: activity.status,
      Type: activity.type,
      Start: activity.start?.toISOString().slice(0, 10) ?? '',
      Finish: activity.finish?.toISOString().slice(0, 10) ?? '',
      'Actual Start': activity.actualStart?.toISOString().slice(0, 10) ?? '',
      'Actual Finish': activity.actualFinish?.toISOString().slice(0, 10) ?? '',
      'Original Duration (d)': activity.originalDurationDays.toFixed(1),
      'Remaining Duration (d)': activity.remainingDurationDays.toFixed(1),
      'Total Float (d)': activity.totalFloatDays.toFixed(1),
      'Free Float (d)': activity.freeFloatDays.toFixed(1),
      Critical: activity.isCritical ? 'Yes' : 'No',
      '% Complete': activity.percentComplete.toFixed(0),
      Constraint: activity.constraintType ?? '',
      Predecessors: activity.predecessors.length,
      Successors: activity.successors.length,
      'Budget Cost': activity.budgetCost.toFixed(2),
      'Actual Cost': activity.actualCost.toFixed(2),
      'Baseline Finish':
        baselineByCode?.get(activity.code)?.finish?.toISOString().slice(0, 10) ?? '',
    }));

    const blob = new Blob([Papa.unparse(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${schedule?.projectName ?? 'schedule'}-activities.csv`.replace(
      /[^a-z0-9.\-]/gi,
      '_'
    );
    link.click();
    URL.revokeObjectURL(url);
  }, [filtered, baselineByCode, schedule]);

  /* ── AI narrative ────────────────────────────────────────────────────── */

  const generateNarrative = useCallback(
    async (model: ScheduleModel, result: DCMAResult, evmSummary: ReturnType<typeof computeEvm>) => {
      setNarrativeState('loading');

      const failing = result.checks.filter(
        (c) => c.status === 'fail' || c.status === 'warning'
      );

      // A compact factual brief, so the model interprets numbers rather than
      // inventing them.
      const brief = {
        project: model.projectName,
        dataDate: model.dataDate?.toISOString().slice(0, 10) ?? 'unknown',
        dataDateInferred: model.dataDateInferred,
        activities: model.activities.length,
        relationships: model.relationships.length,
        complete: model.activities.filter((a) => a.isComplete).length,
        inProgress: model.activities.filter((a) => a.isInProgress).length,
        criticalIncomplete: model.activities.filter((a) => a.isCritical && !a.isComplete)
          .length,
        negativeFloat: model.activities.filter(
          (a) => !a.isComplete && a.totalFloatDays < 0
        ).length,
        projectFinish: model.finish?.toISOString().slice(0, 10) ?? 'unknown',
        dcmaScore: `${result.passed}/${result.assessable}`,
        dcmaFailures: failing.map((c) => `${c.name}: ${c.value} (target ${c.threshold})`),
        spi: evmSummary.spi?.toFixed(2) ?? 'n/a',
        cpi: evmSummary.cpi?.toFixed(2) ?? 'n/a',
        hasCost: evmSummary.hasCost,
        baselineSlipDays: comparison?.projectSlipDays ?? 'no baseline',
      };

      const prompt = `You are a chartered project controls consultant writing the summary page of a schedule assurance review for a client.

Here are the facts extracted from the schedule. Do not invent numbers beyond these:
${JSON.stringify(brief, null, 2)}

Write markdown with exactly these sections:

## Verdict
Two or three sentences: is this schedule fit to manage the work? Lead with the answer.

## What the numbers say
Four to six bullets interpreting the metrics above. Explain what each finding means for delivery, not what the metric is called. Quote the figures.

## Where to look first
Two or three prioritised actions, most material first. Be specific about which checks or activities to interrogate.

## Caveats
One or two bullets on what this analysis cannot see (for example: no baseline, inferred data date, no cost loading) — only where true given the facts above.

British English. No preamble, no closing pleasantries. Be direct and specific; a planner will read this.`;

      try {
        const response = await chatService.sendMessage([{ role: 'user', content: prompt }]);
        setNarrative(response);
        setNarrativeState('idle');
      } catch {
        setNarrativeState('error');
      }
    },
    [comparison]
  );

  const handleScheduleLoaded = useCallback(
    (model: ScheduleModel) => {
      setSchedule(model);
      setFilters(EMPTY_FILTERS);
      setSelectedId(null);
      setTab('overview');

      const result = new DCMAAnalyzer(model, baseline ?? undefined).analyze();
      const evmSummary = computeEvm(model, baseline ?? undefined);
      void generateNarrative(model, result, evmSummary);
    },
    [baseline, generateNarrative]
  );

  const handleBaselineLoaded = useCallback(
    (model: ScheduleModel) => {
      setBaseline(model);
      if (schedule) {
        const result = new DCMAAnalyzer(schedule, model).analyze();
        const evmSummary = computeEvm(schedule, model);
        void generateNarrative(schedule, result, evmSummary);
      }
    },
    [schedule, generateNarrative]
  );

  /* ── Render ──────────────────────────────────────────────────────────── */

  return (
    <TooltipProvider>
      <div
        className="workspace flex min-h-screen flex-col"
        data-theme={theme}
        data-surface="themed"
      >
        <div data-theme="dark">
          <Header />
        </div>

        {!schedule ? (
          <EmptyState onLoaded={handleScheduleLoaded} />
        ) : (
          <>
            <CommandBar
              ref={searchRef}
              schedule={schedule}
              baseline={baseline}
              filters={filters}
              onFiltersChange={setFilters}
              matchedCount={filtered.length}
              theme={theme}
              onToggleTheme={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
              onExport={handleExport}
              onReset={() => setFilters(EMPTY_FILTERS)}
            />

            <main className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-6 lg:px-8">
              {schedule.warnings.length > 0 && (
                <div
                  className="mb-5 flex items-start gap-3 rounded-sm p-3"
                  style={{ background: 'var(--status-warn-soft)' }}
                >
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 shrink-0"
                    style={{ color: 'var(--status-warn)' }}
                  />
                  <ul className="space-y-1 text-xs" style={{ color: 'var(--status-warn)' }}>
                    {schedule.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Signal row */}
              <SignalRow
                schedule={schedule}
                dcma={dcma}
                evm={evm}
                comparison={comparison}
                headlines={headlines}
                onDrillThrough={drillThrough}
                onGoToTab={setTab}
              />

              {/* Tabs */}
              <div className="tab-bar mt-6" role="tablist">
                {TABS.map((tabDef, index) => {
                  const Icon = tabDef.icon;
                  return (
                    <button
                      key={tabDef.key}
                      role="tab"
                      aria-selected={tab === tabDef.key}
                      className="tab"
                      onClick={() => setTab(tabDef.key)}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {tabDef.label}
                      <span className="tab-count">{index + 1}</span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 space-y-5">
                {tab === 'overview' && (
                  <>
                    <Panel
                      title="Programme narrative"
                      note="AI-generated · verify before use"
                      accent
                    >
                      {narrativeState === 'loading' ? (
                        <div className="space-y-2 p-5">
                          <div className="skeleton h-4 w-1/3" />
                          <div className="skeleton h-3 w-full" />
                          <div className="skeleton h-3 w-11/12" />
                          <div className="skeleton h-3 w-4/5" />
                        </div>
                      ) : narrativeState === 'error' ? (
                        <div className="flex items-center justify-between gap-4 p-5">
                          <p className="text-sm" style={{ color: 'var(--status-warn)' }}>
                            The narrative service is unavailable. Every metric on this page
                            is computed locally and is unaffected.
                          </p>
                          <button
                            className="icon-button shrink-0"
                            onClick={() =>
                              dcma &&
                              evm &&
                              void generateNarrative(schedule, dcma, evm)
                            }
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            Retry
                          </button>
                        </div>
                      ) : (
                        <div className="prose max-w-none p-5 text-sm">
                          <ReactMarkdown>{narrative}</ReactMarkdown>
                        </div>
                      )}
                    </Panel>

                    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                      <Panel
                        title="Total float distribution"
                        note={`${headlines.negativeFloatCount} negative`}
                      >
                        <div className="p-4">
                          <FloatHistogram
                            bins={floatBins}
                            onSelectBin={drillThrough}
                          />
                        </div>
                      </Panel>

                      <Panel
                        title={evm?.hasCost ? 'Cost S-curve' : 'Progress S-curve'}
                        note={evm?.hasCost ? 'BCWS / BCWP / ACWP' : 'activity count'}
                      >
                        <div className="p-4">
                          {evm && <EvmCurve evm={evm} dataDate={schedule.dataDate} />}
                        </div>
                      </Panel>
                    </div>

                    <Panel title="WBS breakdown" note="area = size · colour = risk">
                      <div className="p-4">
                        <WbsTreemap
                          roots={wbsRollup}
                          hasCost={!!evm?.hasCost}
                          onSelectNode={drillThrough}
                        />
                      </div>
                    </Panel>
                  </>
                )}

                {tab === 'quality' && dcma && (
                  <>
                    <DcmaScorecard result={dcma} onDrillThrough={drillThrough} />
                    <Panel title="Total float distribution">
                      <div className="p-4">
                        <FloatHistogram bins={floatBins} onSelectBin={drillThrough} />
                      </div>
                    </Panel>
                  </>
                )}

                {tab === 'critical' && criticalPath && (
                  <>
                    <Panel
                      title="Driving path logic"
                      note={`${criticalPath.chain.length} activities`}
                      accent
                    >
                      <div className="p-4">
                        <CriticalPathNetwork
                          criticalPath={criticalPath}
                          selectedActivityId={selectedId}
                          onSelectActivity={setSelectedId}
                        />
                      </div>
                    </Panel>

                    <Panel title="Critical activities in sequence">
                      <ActivityTable
                        activities={criticalPath.chain}
                        selectedActivityId={selectedId}
                        onSelectActivity={setSelectedId}
                      />
                    </Panel>
                  </>
                )}

                {tab === 'baseline' &&
                  (comparison ? (
                    <>
                      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                        <KpiTile
                          label="Project finish movement"
                          value={
                            comparison.projectSlipDays === null
                              ? '—'
                              : `${comparison.projectSlipDays > 0 ? '+' : ''}${comparison.projectSlipDays}`
                          }
                          numeric={comparison.projectSlipDays ?? null}
                          unit=" days"
                          tone={
                            (comparison.projectSlipDays ?? 0) > 0
                              ? 'fail'
                              : (comparison.projectSlipDays ?? 0) < 0
                                ? 'pass'
                                : 'neutral'
                          }
                          sub={`Baseline ${formatDate(baseline?.finish ?? null)} → current ${formatDate(schedule.finish)}`}
                        />
                        <KpiTile
                          label="Activities slipped"
                          value={String(comparison.slippedCount)}
                          numeric={comparison.slippedCount}
                          tone={comparison.slippedCount > 0 ? 'warn' : 'pass'}
                          sub={`${comparison.improvedCount} pulled earlier · mean ${comparison.meanSlipDays.toFixed(1)} days`}
                          onClick={() =>
                            drillThrough(
                              comparison.entries
                                .filter((e) => e.slipDays > 0)
                                .map((e) => e.activityId),
                              'Slipped vs baseline'
                            )
                          }
                        />
                        <KpiTile
                          label="Scope change"
                          value={`+${comparison.added.length} / −${comparison.removed.length}`}
                          tone={
                            comparison.added.length + comparison.removed.length > 0
                              ? 'warn'
                              : 'pass'
                          }
                          sub="Activities added / removed against baseline"
                          onClick={() =>
                            comparison.added.length > 0 &&
                            drillThrough(
                              comparison.added.map((a) => a.activityId),
                              'Added since baseline'
                            )
                          }
                        />
                      </div>

                      <Panel
                        title="Finish variance against baseline"
                        note="ranked by movement"
                        accent
                      >
                        <div className="p-4">
                          <SlipWaterfall
                            entries={comparison.entries}
                            onSelectActivity={setSelectedId}
                          />
                        </div>
                      </Panel>

                      <Panel title="Schedule against baseline">
                        <div className="p-4">
                          <ScheduleGantt
                            activities={filtered}
                            baselineByCode={baselineByCode}
                            dataDate={schedule.dataDate}
                            selectedActivityId={selectedId}
                            onSelectActivity={setSelectedId}
                          />
                        </div>
                      </Panel>
                    </>
                  ) : (
                    <Panel title="Baseline variance">
                      <div className="p-8">
                        <p
                          className="mb-4 text-sm"
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          Upload a baseline schedule to unlock finish variance, missed
                          tasks, BEI and scope change.
                        </p>
                        <div className="max-w-md">
                          <ScheduleUpload
                            onLoaded={handleBaselineLoaded}
                            isBaseline
                            loaded={baseline}
                            onClear={() => setBaseline(null)}
                          />
                        </div>
                      </div>
                    </Panel>
                  ))}

                {tab === 'cost' && evm && (
                  <>
                    <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
                      <KpiTile
                        label={evm.hasCost ? 'Budget at completion' : 'Total activities'}
                        value={
                          evm.hasCost
                            ? formatCurrency(evm.budgetAtCompletion)
                            : String(Math.round(evm.budgetAtCompletion))
                        }
                        tone="neutral"
                        sub={evm.hasCost ? 'From resource assignments' : 'No cost data in file'}
                      />
                      <KpiTile
                        label="Earned value"
                        value={
                          evm.hasCost
                            ? formatCurrency(evm.earnedValue)
                            : String(Math.round(evm.earnedValue))
                        }
                        tone="info"
                        sub={`Planned ${
                          evm.hasCost
                            ? formatCurrency(evm.plannedValue)
                            : Math.round(evm.plannedValue)
                        } at data date`}
                      />
                      <KpiTile
                        label="Schedule variance"
                        value={
                          evm.hasCost
                            ? formatCurrency(evm.scheduleVariance)
                            : Math.round(evm.scheduleVariance).toString()
                        }
                        tone={evm.scheduleVariance < 0 ? 'fail' : 'pass'}
                        sub="Earned minus planned"
                      />
                      <KpiTile
                        label="Cost variance"
                        value={
                          evm.hasCost ? formatCurrency(evm.costVariance) : 'n/a'
                        }
                        tone={
                          !evm.hasCost
                            ? 'neutral'
                            : evm.costVariance < 0
                              ? 'fail'
                              : 'pass'
                        }
                        sub={
                          evm.hasCost
                            ? `EAC ${formatCurrency(evm.estimateAtCompletion ?? 0)}`
                            : 'Requires cost loading'
                        }
                      />
                    </div>

                    <Panel
                      title={evm.hasCost ? 'Earned value S-curve' : 'Progress S-curve'}
                      accent
                    >
                      <div className="p-4">
                        <EvmCurve evm={evm} dataDate={schedule.dataDate} />
                      </div>
                    </Panel>

                    <Panel title="WBS cost and float" note="area = size · colour = risk">
                      <div className="p-4">
                        <WbsTreemap
                          roots={wbsRollup}
                          hasCost={evm.hasCost}
                          onSelectNode={drillThrough}
                        />
                      </div>
                    </Panel>
                  </>
                )}

                {tab === 'activities' && (
                  <>
                    <Panel title="Programme Gantt" note="baseline ghosts · float tails">
                      <div className="p-4">
                        <ScheduleGantt
                          activities={filtered}
                          baselineByCode={baselineByCode}
                          dataDate={schedule.dataDate}
                          selectedActivityId={selectedId}
                          onSelectActivity={setSelectedId}
                        />
                      </div>
                    </Panel>

                    <Panel
                      title="Activity register"
                      note={`${filtered.length} rows · click any row`}
                    >
                      <ActivityTable
                        activities={filtered}
                        selectedActivityId={selectedId}
                        onSelectActivity={setSelectedId}
                      />
                    </Panel>
                  </>
                )}
              </div>

              {/* Files */}
              <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
                <ScheduleUpload
                  onLoaded={handleScheduleLoaded}
                  loaded={schedule}
                  onClear={() => {
                    setSchedule(null);
                    setNarrative('');
                    setFilters(EMPTY_FILTERS);
                    setSelectedId(null);
                  }}
                />
                <ScheduleUpload
                  onLoaded={handleBaselineLoaded}
                  isBaseline
                  loaded={baseline}
                  onClear={() => setBaseline(null)}
                />
              </div>

              <p className="mt-6 text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                Files are parsed in your browser and discarded on refresh — nothing is
                uploaded. Only the anonymised metric summary above is sent to Movar
                Reply's Azure environment to generate the narrative. Every figure is a
                draft for a planner to verify before it reaches a client.
              </p>
            </main>
          </>
        )}

        {selected && schedule && (
          <ActivityInspector
            activity={selected}
            schedule={schedule}
            baseline={baselineByCode?.get(selected.code)}
            onClose={() => setSelectedId(null)}
            onNavigate={setSelectedId}
          />
        )}

        <footer className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
          <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-3 px-4 py-6 md:flex-row md:items-center md:gap-8 lg:px-8">
            <p className="eyebrow" style={{ color: 'var(--text-muted)' }}>
              &copy; 2026 Movar Reply
            </p>
            <p className="text-sm italic" style={{ color: 'var(--text-muted)' }}>
              big enough to deliver, small enough to care
            </p>
            <p className="eyebrow md:ml-auto" style={{ color: 'var(--text-muted)' }}>
              A Reply Group company
            </p>
          </div>
        </footer>
      </div>
    </TooltipProvider>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────────── */

function Panel({
  title,
  note,
  accent,
  children,
}: {
  title: string;
  note?: string;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={`panel ${accent ? 'panel-accent' : ''}`}>
      <div className="panel-header">
        <h3 className="panel-title">{title}</h3>
        {note && <span className="panel-note">{note}</span>}
      </div>
      {children}
    </section>
  );
}

function SignalRow({
  schedule,
  dcma,
  evm,
  comparison,
  headlines,
  onDrillThrough,
  onGoToTab,
}: {
  schedule: ScheduleModel;
  dcma: DCMAResult | null;
  evm: ReturnType<typeof computeEvm> | null;
  comparison: ReturnType<typeof compareToBaseline> | null;
  headlines: ReturnType<typeof computeHeadlines>;
  onDrillThrough: (ids: string[], label: string) => void;
  onGoToTab: (tab: TabKey) => void;
}) {
  const cpli = dcma?.checks.find((c) => c.key === 'cpli');
  const bei = dcma?.checks.find((c) => c.key === 'bei');

  const indexTone = (value: number | null | undefined): KpiTone => {
    if (value === null || value === undefined) return 'neutral';
    if (value >= 0.95) return 'pass';
    return value >= 0.85 ? 'warn' : 'fail';
  };

  const dcmaTone: KpiTone = dcma
    ? dcma.score >= 0.85
      ? 'pass'
      : dcma.score >= 0.6
        ? 'warn'
        : 'fail'
    : 'neutral';

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
      <KpiTile
        label="Finish variance"
        value={
          comparison?.projectSlipDays === null || comparison === null
            ? '—'
            : `${comparison.projectSlipDays > 0 ? '+' : ''}${comparison.projectSlipDays}`
        }
        numeric={comparison?.projectSlipDays ?? null}
        unit={comparison ? 'd' : undefined}
        tone={
          !comparison
            ? 'neutral'
            : (comparison.projectSlipDays ?? 0) > 0
              ? 'fail'
              : 'pass'
        }
        sub={
          comparison
            ? `vs baseline · finish ${formatDate(schedule.finish)}`
            : 'Upload a baseline to measure'
        }
        onClick={() => onGoToTab('baseline')}
      />

      <KpiTile
        label="SPI"
        value={evm?.spi?.toFixed(2) ?? '—'}
        numeric={evm?.spi ?? null}
        precision={2}
        tone={indexTone(evm?.spi)}
        sub={evm?.spi ? 'Earned ÷ planned at data date' : 'Insufficient data'}
        onClick={() => onGoToTab('cost')}
      />

      <KpiTile
        label="CPI"
        value={evm?.cpi?.toFixed(2) ?? 'n/a'}
        numeric={evm?.cpi ?? null}
        precision={2}
        tone={indexTone(evm?.cpi)}
        sub={evm?.hasCost ? 'Earned ÷ actual cost' : 'No cost loading in file'}
        onClick={() => onGoToTab('cost')}
      />

      <KpiTile
        label="CPLI"
        value={cpli?.value ?? '—'}
        numeric={cpli?.numeric ?? null}
        precision={2}
        tone={indexTone(cpli?.numeric)}
        sub={`${headlines.criticalCount} critical activities`}
        onClick={() => onGoToTab('critical')}
      />

      <KpiTile
        label="BEI"
        value={bei?.value ?? 'n/a'}
        numeric={bei?.numeric ?? null}
        precision={2}
        tone={indexTone(bei?.numeric)}
        sub={
          bei?.status === 'na'
            ? 'Upload a baseline to measure'
            : 'Finished ÷ due by data date'
        }
        onClick={() =>
          bei?.failingActivityIds.length
            ? onDrillThrough(bei.failingActivityIds, 'Behind baseline')
            : onGoToTab('quality')
        }
      />

      <KpiTile
        label="DCMA score"
        value={dcma ? `${dcma.passed}/${dcma.assessable}` : '—'}
        tone={dcmaTone}
        sub={
          dcma
            ? `${dcma.checks.filter((c) => c.status === 'fail').length} failing checks`
            : ''
        }
        onClick={() => onGoToTab('quality')}
      />
    </div>
  );
}

function EmptyState({ onLoaded }: { onLoaded: (model: ScheduleModel) => void }) {
  return (
    <main className="flex-1" data-theme="dark">
      <div className="relative overflow-hidden">
        <div className="plot-grid pointer-events-none absolute inset-0" aria-hidden="true" />

        <div className="relative mx-auto max-w-4xl px-6 py-20 text-center lg:px-12">
          <p className="eyebrow mb-5" style={{ color: 'var(--mr-vivid-blue)' }}>
            Schedule assurance · DCMA 14-point · earned value
          </p>
          <h1 className="display-lg mb-5 text-white">
            Upload a programme.
            <br />
            Get the read in seconds.
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg leading-snug text-white/65">
            Drop a Primavera P6 export and this parses the logic, float, constraints,
            resources and costs, then runs the full DCMA 14-point assessment against the
            schedule's own data date. Everything happens in your browser.
          </p>

          <div className="mx-auto max-w-lg">
            <ScheduleUpload onLoaded={onLoaded} />
          </div>

          <div className="mt-14 grid grid-cols-1 gap-8 text-left sm:grid-cols-3">
            {[
              {
                title: 'Schedule quality',
                body: 'All 14 DCMA checks — logic, leads, lags, link types, hard constraints, float, durations, dates, resources, missed tasks, critical path, CPLI and BEI. Every check drills through to the activities that failed it.',
              },
              {
                title: 'Critical path and float',
                body: 'Driving-path logic diagram with lag annotated, float distribution against the 44-day threshold, and a Gantt with baseline ghosts and float tails.',
              },
              {
                title: 'Earned value',
                body: 'Cost S-curve with BCWS, BCWP and ACWP where the file is cost-loaded, SPI and CPI, and a WBS treemap sized by cost and coloured by risk.',
              },
            ].map((item) => (
              <div key={item.title}>
                <div className="mb-3 h-px w-10" style={{ background: 'var(--mr-vivid-blue)' }} />
                <h3 className="mb-2 text-sm font-bold text-white">{item.title}</h3>
                <p className="text-sm leading-relaxed text-white/55">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
